import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { StepType, StepState } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface StepIndicatorProps {
  currentStep: StepType;
  stepStates: StepState[];
  onStepClick: (step: StepType) => void;
}

const stepNames = ['过程', '对象', '连接', '拆解', '翻译'];

export default function StepIndicator({ 
  currentStep, 
  stepStates, 
  onStepClick 
}: StepIndicatorProps) {
  const { theme } = useTheme();
  const isQinghua = theme === 'qinghua';

  // 青花瓷主题使用主题变量
  if (isQinghua) {
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((step, index) => {
          const stepState = stepStates.find(s => s.step === step);
          const isActive = currentStep === step;
          const isCompleted = stepState?.completed;
          
          return (
            <div key={step} className="flex items-center">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onStepClick(step as StepType)}
                className="relative w-10 h-10 rounded-full font-bold text-sm flex items-center justify-center transition-all duration-300"
                style={{
                  background: isActive 
                    ? 'linear-gradient(135deg, #2E5AA8 0%, #1E4080 100%)'
                    : isCompleted
                      ? 'linear-gradient(135deg, #2E5AA8 0%, #4A7AC7 100%)'
                      : '#FFFDF8',
                  color: isActive || isCompleted ? '#FFFDF8' : '#718096',
                  border: isActive 
                    ? '2px solid #C9A961'
                    : isCompleted
                      ? '2px solid #C9A961'
                      : '2px solid rgba(46, 90, 168, 0.3)',
                  boxShadow: isActive 
                    ? '0 4px 15px rgba(46, 90, 168, 0.3), 0 0 0 3px rgba(201, 169, 97, 0.2)'
                    : '0 2px 8px rgba(46, 90, 168, 0.1)',
                  fontFamily: "'Noto Serif SC', serif"
                }}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  step
                )}
                
                {/* 步骤名称提示 */}
                <span 
                  className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap transition-opacity duration-200"
                  style={{
                    color: isActive ? '#2E5AA8' : '#718096',
                    opacity: isActive ? 1 : 0,
                    fontFamily: "'Noto Serif SC', serif"
                  }}
                >
                  {stepNames[index]}
                </span>
              </motion.button>
              
              {/* 连接线 */}
              {index < 4 && (
                <div 
                  className="w-8 h-0.5 mx-1"
                  style={{
                    background: stepStates[index]?.completed 
                      ? 'linear-gradient(90deg, #2E5AA8, #4A7AC7)' 
                      : 'rgba(46, 90, 168, 0.2)'
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // 默认主题
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((step, index) => {
        const stepState = stepStates.find(s => s.step === step);
        const isActive = currentStep === step;
        const isCompleted = stepState?.completed;
        
        return (
          <div key={step} className="flex items-center">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onStepClick(step as StepType)}
              className={`
                relative w-10 h-10 rounded-full font-display font-bold text-sm
                flex items-center justify-center transition-all duration-300
                ${isActive 
                  ? 'step-indicator active' 
                  : isCompleted
                    ? 'step-indicator completed'
                    : 'step-indicator'
                }
              `}
            >
              {isCompleted ? (
                <Check className="w-5 h-5" />
              ) : (
                step
              )}
              
              {/* 步骤名称提示 */}
              <span className={`
                absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap
                transition-opacity duration-200
              `}
              style={{
                color: isActive ? 'var(--theme-accent)' : 'var(--theme-text-muted)',
                opacity: isActive ? 1 : 0
              }}
              >
                {stepNames[index]}
              </span>
            </motion.button>
            
            {/* 连接线 */}
            {index < 4 && (
              <div 
                className="w-8 h-0.5 mx-1"
                style={{
                  background: stepStates[index]?.completed 
                    ? 'linear-gradient(90deg, #22c55e, #4ade80)' 
                    : 'var(--theme-border)'
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

